<script>
/**
 * @component UrgencyConfig
 * @description Configuración de especialidades de urgencia. CRUD completo con tabla,
 *              diálogo de alta/edición, toggle de habilitado/deshabilitado y eliminación
 *              con confirmación. Solo accesible para admin/health-admin.
 * @example <UrgencyConfig />
 */
import urgencyConfigService from "@/services/urgency-config";
import api from "@/services/api";
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import { mapGetters } from "vuex";

export default {
  name: "UrgencyConfig",
  components: { Spinner, BaseSubNav },

  data() {
    return {
      isLoading: false,
      items: [],
      specialties: [],
      isDialogVisible: false,
      dialogMode: "create", // 'create' | 'edit'
      form: {
        medicalSpecialtyId: "",
        description: "",
        order: 0,
        enabled: true,
      },
      editingId: null,
      formErrors: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /**
     * @description Items ordenados por campo order ascendente
     * @returns {Array}
     */
    sortedItems() {
      return [...this.items].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
    },
  },

  created() {
    this.fetchItems();
    this.fetchSpecialties();
  },

  methods: {
    /**
     * @description Carga las configuraciones de urgencia desde la API
     * @returns {Promise<void>}
     */
    async fetchItems() {
      this.isLoading = true;
      try {
        this.items = await urgencyConfigService.getAll();
      } catch (e) {
        this.$message.error("Error al cargar configuraciones de urgencia");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Carga las especialidades médicas disponibles
     * @returns {Promise<void>}
     */
    async fetchSpecialties() {
      try {
        const res = await api.get("/medical-specialty");
        this.specialties = res.data.docs || res.data;
      } catch (e) {
        console.error("Error fetching specialties", e);
      }
    },

    /**
     * @description Obtiene el nombre de la especialidad médica a partir de un item
     * @param {Object} item - Configuración de urgencia
     * @returns {string}
     */
    getSpecialtyName(item) {
      if (item.medicalSpecialty && item.medicalSpecialty.name) {
        return item.medicalSpecialty.name;
      }
      const spec = this.specialties.find(
        (s) => s._id === item.medicalSpecialtyId
      );
      return spec ? spec.name : "—";
    },

    /**
     * @description Abre el diálogo de creación o edición
     * @param {string} mode - 'create' o 'edit'
     * @param {Object|null} item - Item a editar (null para crear)
     */
    openDialog(mode = "create", item = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};

      if (mode === "edit" && item) {
        this.editingId = item._id;
        this.form = {
          medicalSpecialtyId:
            item.medicalSpecialty?._id || item.medicalSpecialtyId || "",
          description: item.description || "",
          order: item.displayOrder || 0,
          enabled: item.isEnabled !== false,
        };
      } else {
        this.editingId = null;
        this.form = {
          medicalSpecialtyId: "",
          description: "",
          order: this.items.length + 1,
          enabled: true,
        };
      }
    },

    /**
     * @description Cierra el diálogo y limpia el formulario
     */
    closeDialog() {
      this.isDialogVisible = false;
      this.editingId = null;
      this.form = {
        medicalSpecialtyId: "",
        description: "",
        order: 0,
        enabled: true,
      };
      this.formErrors = {};
    },

    /**
     * @description Construye el payload con los nombres de campo que espera el backend
     * @returns {Object}
     */
    buildPayload() {
      return {
        medicalSpecialtyId: this.form.medicalSpecialtyId,
        description: this.form.description,
        displayOrder: this.form.order,
        isEnabled: this.form.enabled,
      };
    },

    /**
     * @description Guarda (crea o actualiza) la configuración de urgencia
     * @returns {Promise<void>}
     */
    async saveItem() {
      this.formErrors = {};
      if (!this.form.medicalSpecialtyId) {
        this.formErrors.medicalSpecialtyId = "Especialidad requerida";
      }
      if (Object.keys(this.formErrors).length) return;

      try {
        const payload = this.buildPayload();
        if (this.dialogMode === "create") {
          await urgencyConfigService.create(payload);
          this.$message.success("Especialidad de urgencia creada");
        } else {
          await urgencyConfigService.update(this.editingId, payload);
          this.$message.success("Especialidad de urgencia actualizada");
        }
        this.closeDialog();
        this.fetchItems();
      } catch (e) {
        this.$message.error("Error al guardar");
      }
    },

    /**
     * @description Alterna el estado habilitado/deshabilitado de un item
     * @param {Object} item - Configuración a togglear
     * @returns {Promise<void>}
     */
    async toggleEnabled(item) {
      try {
        await urgencyConfigService.update(item._id, {
          isEnabled: !item.isEnabled,
        });
        item.isEnabled = !item.isEnabled;
        this.$message.success(
          item.isEnabled
            ? "Especialidad habilitada"
            : "Especialidad deshabilitada"
        );
      } catch (e) {
        this.$message.error("Error al actualizar estado");
      }
    },

    /**
     * @description Elimina una configuración de urgencia con confirmación
     * @param {Object} item - Configuración a eliminar
     * @returns {Promise<void>}
     */
    async deleteItem(item) {
      const name = this.getSpecialtyName(item);
      try {
        await this.$confirm(
          `¿Eliminar la especialidad "${name}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await urgencyConfigService.remove(item._id);
        this.$message.success("Especialidad eliminada");
        this.fetchItems();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
section.urgency-config
  BaseSubNav(title="Configuración de Urgencias")

  .urgency-config__header
    button.button-teal-new(@click="openDialog('create')")
      micon(name="add_circle_outline")
      span Agregar especialidad

  .urgency-config__body
    .box.box--with-subnav
      .box__content--stretch
        spinner(v-if="isLoading")
        table(v-else-if="sortedItems.length")
          thead
            tr
              th Especialidad
              th Descripción
              th Orden
              th Estado
              th Acciones
          tbody
            tr(v-for="item in sortedItems" :key="item._id")
              td.td--specialty {{ getSpecialtyName(item) }}
              td {{ item.description }}
              td.td--order {{ item.displayOrder }}
              td.td--status
                el-switch(
                  :value="item.isEnabled"
                  active-color="#0d9d9d"
                  inactive-color="#cbd5e1"
                  @change="toggleEnabled(item)"
                )
              td.td--actions
                .actions-container
                  button.button.button--action(@click="openDialog('edit', item)")
                    micon(name="edit")
                  button.button.button--action.button--action-danger(@click="deleteItem(item)")
                    micon(name="delete_forever")

        .urgency-config__empty(v-if="!isLoading && !sortedItems.length")
          micon(name="alert")
          p No hay especialidades de urgencia configuradas

  el-dialog(
    :title="dialogMode === 'create' ? 'Nueva Especialidad de Urgencia' : 'Editar Especialidad de Urgencia'"
    :visible.sync="isDialogVisible"
    width="560px"
  )
    el-form(label-position="top" size="small")
      el-form-item(label="Especialidad" required :error="formErrors.medicalSpecialtyId")
        el-select(
          v-model="form.medicalSpecialtyId"
          placeholder="Seleccionar especialidad"
          filterable
          style="width: 100%"
        )
          el-option(
            v-for="s in specialties"
            :key="s._id"
            :label="s.name"
            :value="s._id"
          )
      el-form-item(label="Descripción")
        el-input(type="textarea" v-model="form.description" maxlength="300" rows="2" placeholder="Descripción de la especialidad")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="Orden")
            el-input-number(v-model="form.order" :min="0" :max="999" controls-position="right")
        el-col(:span="12")
          el-form-item(label="Estado")
            el-switch(v-model="form.enabled" active-text="Habilitado" inactive-text="Deshabilitado" active-color="#0d9d9d")
    template(#footer)
      el-button(@click="closeDialog") Cancelar
      el-button(type="primary" @click="saveItem") Guardar
</template>

<style lang="scss" scoped>
.urgency-config {
  &__header {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;

    .button-teal-new {
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .micon {
        width: 16px;
        height: 16px;
      }
    }
  }

  &__body {
    .box__content--stretch {
      overflow-x: auto;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
    color: $text-muted-new;

    .micon {
      width: 40px;
      height: 40px;
      fill: $text-muted-new;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}

.td--specialty {
  font-weight: 600;
  color: $navy;
}

.td--order {
  text-align: center;
  width: 80px;
}

.td--status {
  width: 90px;
}

.td--actions {
  width: 100px;
}

.actions-container {
  display: flex;
  gap: 4px;
}

.button--action {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  .micon {
    width: 18px;
    height: 18px;
    fill: $text-secondary-new;
  }

  &:hover {
    background: #f1f5f9;
    .micon {
      fill: $navy;
    }
  }

  &-danger:hover {
    background: rgba(239, 68, 68, 0.08);
    .micon {
      fill: #ef4444;
    }
  }
}
</style>
